/**
 * CollapsibleSection Component
 *
 * Reusable collapsible section with modern UI
 * Production-grade with animations and accessibility
 */

import React, { useState } from "react";
import { HiOutlineChevronDown, HiOutlineChevronUp } from "react-icons/hi";

const CollapsibleSection = ({
  title,
  children,
  defaultCollapsed = true,
  icon = null,
  badge = null,
  className = "",
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${className}`}>
      {/* Header */}
      <button
        className="w-full flex items-center justify-between px-6 py-4 bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 hover:to-gray-50 transition-all duration-200 group"
        onClick={toggleCollapse}
        aria-expanded={!isCollapsed}
        aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${title}`}
      >
        <div className="flex items-center space-x-3">
          {/* Toggle Icon */}
          <div className="transition-transform duration-200 group-hover:scale-110">
            {isCollapsed ? (
              <HiOutlineChevronDown className="w-5 h-5 text-gray-600" />
            ) : (
              <HiOutlineChevronUp className="w-5 h-5 text-blue-600" />
            )}
          </div>

          {/* Custom Icon (optional) */}
          {icon && (
            <div className="text-blue-600">
              {icon}
            </div>
          )}

          {/* Title */}
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
            {title}
          </h2>

          {/* Badge (optional) */}
          {badge && (
            <span className="ml-2 px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">
              {badge}
            </span>
          )}
        </div>

        {/* Collapse Indicator */}
        <div className="text-xs text-gray-500 hidden sm:block">
          {isCollapsed ? "Click to expand" : "Click to collapse"}
        </div>
      </button>

      {/* Content with smooth animation */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          isCollapsed ? "max-h-0 opacity-0" : "max-h-[5000px] opacity-100"
        } overflow-hidden`}
      >
        <div className="p-6 bg-white">
          {children}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSection;
